"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { getCookie } from "cookies-next/client";
import { lazy, Suspense, type Usable } from "react";
import { SlBag } from "react-icons/sl";
import type { ResolvedIntlConfig } from "react-intl";
import { useAnalytics } from "use-analytics";
import { LoadingDots } from "@repo/components/atoms/loading-dots";
import { Price } from "@repo/components/atoms/price";
import { readFragmentShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { BaseCartLineConnectionMasked_Fragment, Cart_Fragment } from "#/graphql/shopify-storefront/fragments";
import { CartQueryOrCartCreateMutationHandler } from "#/graphql/shopify-storefront/handlers";
import { useGetIntl } from "@repo/i18n/intl/client";
import { BaseCartLine } from "./base-cart-line";
const AddPremiumPackaging = lazy(() => import("#/components/molecules/cart/add-premium-packaging"));
export function CartEmpty(t0) {
  const $ = _c(8);
  const {
    dictionaryPromise,
    lang
  } = t0;
  let t1;
  if ($[0] !== dictionaryPromise || $[1] !== lang) {
    t1 = {
      dictionaryPromise,
      lang
    };
    $[0] = dictionaryPromise;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const intl = useGetIntl(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SlBag className="icon fill h-8 w-8" />;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== intl) {
    t3 = intl.formatMessage({
      defaultMessage: "Your cart is empty",
      description: "Your cart is empty",
      id: "bupPhI"
    });
    $[4] = intl;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="grid grid-flow-col gap-4 content-center items-center mx-auto w-fit">{t2}<span className="font-serif">{t3}</span></div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export function CartForm(t0) {
  const $ = _c(90);
  const {
    container,
    dictionaryPromise,
    lang
  } = t0;
  let t1;
  if ($[0] !== dictionaryPromise || $[1] !== lang) {
    t1 = {
      dictionaryPromise,
      lang
    };
    $[0] = dictionaryPromise;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const intl = useGetIntl(t1);
  const {
    track
  } = useAnalytics() || {};
  let t2;
  if ($[3] !== container || $[4] !== track) {
    t2 = () => {
      track("begin_checkout", {
        container
      });
    };
    $[3] = container;
    $[4] = track;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleClickTrack = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getCookie("cartId");
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const cartId = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      enabled: !!cartId,
      queryKey: ["cart", cartId],
      queryFn: async () => {
        const variables = {
          cartId
        };
        const cartMasked = await CartQueryOrCartCreateMutationHandler({
          variables
        });
        return cartMasked;
      },
      staleTime: 5000
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: cartMasked_0,
    isLoading
  } = useQuery(t4);
  if (!cartMasked_0) {
    let t5;
    if ($[8] !== dictionaryPromise || $[9] !== lang) {
      t5 = <CartEmpty dictionaryPromise={dictionaryPromise} lang={lang} />;
      $[8] = dictionaryPromise;
      $[9] = lang;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  let cart;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[11] !== cartMasked_0 || $[12] !== dictionaryPromise || $[13] !== isLoading || $[14] !== lang) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      cart = readFragmentShopifyStorefront(Cart_Fragment, cartMasked_0);
      if (!cart) {
        let t9;
        if ($[20] !== dictionaryPromise || $[21] !== lang) {
          t9 = <CartEmpty dictionaryPromise={dictionaryPromise} lang={lang} />;
          $[20] = dictionaryPromise;
          $[21] = lang;
          $[22] = t9;
        } else {
          t9 = $[22];
        }
        t8 = t9;
        break bb0;
      }
      if (isLoading) {
        let t9;
        if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
          t9 = <div className="fixed top-4 right-4">Loading...</div>;
          $[23] = t9;
        } else {
          t9 = $[23];
        }
        t8 = t9;
        break bb0;
      }
      const {
        baseCartLineConnectionMasked
      } = cart;
      const baseCartLinesMasked = readFragmentShopifyStorefront(BaseCartLineConnectionMasked_Fragment, baseCartLineConnectionMasked)?.edges.map(_temp);
      t7 = "grid gap-4 grid-rows-[1fr_auto] @xl/cart:grid-cols-2";
      t5 = "grid gap-2 content-start overflow-auto";
      t6 = baseCartLinesMasked?.map((baseCartLineMasked_0, i) => <li key={i} className="flex w-full flex-col first:mt-4 not-last:border-b border-neutral-300 dark:border-neutral-700"><BaseCartLine baseCartLineMasked={baseCartLineMasked_0} cartMasked={cartMasked_0} dictionaryPromise={dictionaryPromise} lang={lang} /></li>);
    }
    $[11] = cartMasked_0;
    $[12] = dictionaryPromise;
    $[13] = isLoading;
    $[14] = lang;
    $[15] = cart;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    cart = $[15];
    t5 = $[16];
    t6 = $[17];
    t7 = $[18];
    t8 = $[19];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  let t9;
  if ($[24] !== t5 || $[25] !== t6) {
    t9 = <ul className={t5}>{t6}</ul>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <LoadingDots />;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== dictionaryPromise || $[29] !== lang) {
    t11 = <Suspense fallback={t10}><AddPremiumPackaging className="self-end grid-cols-2 bg-background-muted p-4" dictionaryPromise={dictionaryPromise} lang={lang} /></Suspense>;
    $[28] = dictionaryPromise;
    $[29] = lang;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t7 || $[33] !== t9) {
    t12 = <div className={t7}>{t9}{t11}</div>;
    $[31] = t11;
    $[32] = t7;
    $[33] = t9;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] !== intl) {
    t13 = intl.formatMessage({
      defaultMessage: "Taxes",
      description: "Taxes",
      id: "Gb2+bH"
    });
    $[35] = intl;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t13) {
    t14 = <span className="uppercase">{t13}</span>;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const t15 = cart?.cost.totalAmount?.amount;
  const t16 = cart?.cost.totalAmount?.currencyCode;
  let t17;
  if ($[39] !== lang || $[40] !== t15 || $[41] !== t16) {
    t17 = <Price amount={t15} className="text-right" currencyCode={t16} lang={lang} />;
    $[39] = lang;
    $[40] = t15;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t14 || $[44] !== t17) {
    t18 = <div className="mb-3 flex items-center justify-between border-b border-neutral-200 pb-1 dark:border-neutral-700">{t14}{t17}</div>;
    $[43] = t14;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== intl) {
    t19 = intl.formatMessage({
      defaultMessage: "Total Items",
      description: "Total Items",
      id: "cT/XOj"
    });
    $[46] = intl;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t19) {
    t20 = <span className="uppercase">{t19}</span>;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const t21 = cart?.totalQuantity ?? 0;
  let t22;
  if ($[50] !== t21) {
    t22 = <span className="text-right">{t21}</span>;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t20 || $[53] !== t22) {
    t23 = <div className="flex items-center justify-between">{t20}{t22}</div>;
    $[52] = t20;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== intl) {
    t24 = intl.formatMessage({
      defaultMessage: "Shipping",
      description: "Shipping",
      id: "J9R5IK"
    });
    $[55] = intl;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t24) {
    t25 = <span className="uppercase">{t24}</span>;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <span className="text-right">Calculated at checkout</span>;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t25) {
    t27 = <div className="flex items-center justify-between">{t25}{t26}</div>;
    $[60] = t25;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== intl) {
    t28 = intl.formatMessage({
      defaultMessage: "Total Amount",
      description: "Total Amount",
      id: "bc/cr0"
    });
    $[62] = intl;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t28) {
    t29 = <span className="uppercase">{t28}</span>;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  const t30 = cart?.cost.totalAmount.amount;
  const t31 = cart?.cost.totalAmount.currencyCode;
  let t32;
  if ($[66] !== lang || $[67] !== t30 || $[68] !== t31) {
    t32 = <Price amount={t30} className="text-right" currencyCode={t31} lang={lang} />;
    $[66] = lang;
    $[67] = t30;
    $[68] = t31;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t29 || $[71] !== t32) {
    t33 = <div className="flex items-center justify-between">{t29}{t32}</div>;
    $[70] = t29;
    $[71] = t32;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t23 || $[74] !== t27 || $[75] !== t33) {
    t34 = <section className="grid gap-2 text-sm">{t23}{t27}{t33}</section>;
    $[73] = t23;
    $[74] = t27;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  const t35 = cart?.checkoutUrl;
  let t36;
  if ($[77] !== intl) {
    t36 = intl.formatMessage({
      defaultMessage: "Proceed to Checkout",
      description: "Proceed to Checkout",
      id: "LC+ESC"
    });
    $[77] = intl;
    $[78] = t36;
  } else {
    t36 = $[78];
  }
  let t37;
  if ($[79] !== handleClickTrack || $[80] !== t35 || $[81] !== t36) {
    t37 = <a href={t35} className="btn btn-base btn-bg btn-primary" onClick={handleClickTrack}>{t36}</a>;
    $[79] = handleClickTrack;
    $[80] = t35;
    $[81] = t36;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== t18 || $[84] !== t34 || $[85] !== t37) {
    t38 = <footer className="grid gap-4 text-sm text-neutral-500">{t18}{t34}{t37}</footer>;
    $[83] = t18;
    $[84] = t34;
    $[85] = t37;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== t12 || $[88] !== t38) {
    t39 = <>{t12}{t38}</>;
    $[87] = t12;
    $[88] = t38;
    $[89] = t39;
  } else {
    t39 = $[89];
  }
  return t39;
}
function _temp(t0) {
  const {
    node: baseCartLineMasked
  } = t0;
  return baseCartLineMasked;
}